/**
 * What a change-detail request is, and which change it names.
 *
 * This module held `Inspector`, the change-detail body, until the consumer
 * shell replaced it: `app.ts` routes `/changes/:id` through
 * `changeDetailBody`, which draws `ConsumerChange` and the
 * `change-projection-*` panels. `Inspector` was left behind, exported and
 * called by nothing but its own test.
 *
 * Unrouted is not harmless. It decided whether a change had been accepted
 * with `snapshot.graph.edges.some((edge) => edge.kind === 'accepts')`, and
 * the authority's edge kinds are `base` and `result`; `accepts` exists
 * nowhere but the test fixture, which fabricated one. So the predicate was
 * permanently false, and the four readers of it -- the transition verdict,
 * the canonical accept value, the validation summary and the provenance
 * stepper -- were four wrong answers, held green by the one input that
 * agreed with them. Every routed surface asks `product#acceptedIds`, which
 * folds `certificates[].changes`: rows the authority fills.
 *
 * What survives is the part routed code imports -- the request model. The
 * body is gone rather than repaired, because a second change-detail body is
 * a second answer to one question (B9), and the answer that ships is the one
 * `/changes/:id` draws.
 */

import type { ChangeDetail } from '../generated/surface.js';

import { changeIdFromPath } from './nav.js';

export type ChangeDetailStatus = 'idle' | 'loading' | 'ready' | 'missing' | 'error';

export type ChangeDetailView = {
  detail: ChangeDetail | null;
  status: ChangeDetailStatus;
  error: string | null;
};

/** `?id=` or `/changes/:id` from the address bar. Safe when location is undefined (tests). */
export function requestedChangeId(): string | null {
  if (typeof globalThis.location === 'undefined') return null;
  const fromQuery = new URLSearchParams(globalThis.location.search ?? '').get('id');
  if (fromQuery) return fromQuery;
  return changeIdFromPath(globalThis.location.pathname ?? '');
}
